import { accountrtk } from "api/api-accounts";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch } from "redux/hooks";

type ContactLike = {
  id?: string;
};

export default function useClientUsersDocumentsComplete(
  accountId: string | undefined,
  contacts: ContactLike[] | undefined,
) {
  const dispatch = useAppDispatch();
  const contactIds = useMemo(
    () =>
      (contacts ?? [])
        .map((contact) => contact.id)
        .filter((id): id is string => Boolean(id)),
    [contacts],
  );
  const [documentStatusByContactId, setDocumentStatusByContactId] = useState<
    Record<string, boolean>
  >({});
  const [isCheckingClientUserDocuments, setIsCheckingClientUserDocuments] =
    useState(false);

  useEffect(() => {
    if (!accountId || contactIds.length === 0) {
      setDocumentStatusByContactId({});
      setIsCheckingClientUserDocuments(false);
      return;
    }

    let cancelled = false;
    setIsCheckingClientUserDocuments(true);

    Promise.all(
      contactIds.map(async (contactId) => {
        const response = await dispatch(
          accountrtk.endpoints.fetchMemberPendingDocuments.initiate(
            { user_id: contactId, account_id: accountId },
            { forceRefetch: true },
          ),
        ).unwrap();

        return {
          contactId,
          complete: Boolean(response?.complete),
        };
      }),
    )
      .then((entries) => {
        if (cancelled) {
          return;
        }

        setDocumentStatusByContactId(
          Object.fromEntries(
            entries.map(({ contactId, complete }) => [contactId, complete]),
          ),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setDocumentStatusByContactId({});
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsCheckingClientUserDocuments(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accountId, contactIds, dispatch]);

  const hasClientContacts = contactIds.length > 0;
  const isClientUsersDocumentsComplete =
    hasClientContacts &&
    contactIds.every((contactId) => documentStatusByContactId[contactId]);

  return {
    hasClientContacts,
    isClientUsersDocumentsComplete,
    isCheckingClientUserDocuments,
  };
}
